"use client"
import React from 'react'
import { BottomDrawer } from '@/shared/ui/BottomDrawer'
import type { OpportunityItem } from '../types'
import { useOportunidadeModal } from '../context/OportunidadeModalContext'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { isMasterByEmail } from '@/shared/utils/role'
import { useInOutStatus } from '@/modules/in-out/hooks/useInOutStatus'
import { useInOutFeedback } from '@/modules/in-out/context/InOutFeedbackContext'
import { useSignalsEnabled } from '@/modules/signals/hooks/useSignalsEnabled'
import { useOpportunitySignals } from '@/modules/signals/hooks/useOpportunitySignals'
import { useResultsDrawer } from '@/modules/resultados'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: OpportunityItem | null
  onViewed?: (id: string) => void
  onCopyRequested?: (id: string) => void
  inOutEnabled?: boolean
  resultsEnabled?: boolean
}

export default function OpportunityActionSheet({
  open,
  onOpenChange,
  item,
  onViewed,
  onCopyRequested,
  inOutEnabled = false,
  resultsEnabled = false,
}: Props) {
  const { openModal, openReport, openDebug } = useOportunidadeModal()
  const { user, profile } = useAuth()
  const isMaster = isMasterByEmail(user?.email) || profile?.role === 'master'
  const inOut = useInOutStatus(item ? Number(item.id) : NaN)

  function withClose(action: () => void) {
    return () => {
      onOpenChange(false)
      // dá tempo do drawer fechar antes de abrir detalhes (evita sobreposição)
      setTimeout(action, 180)
    }
  }

  const { showIn, showOut } = useInOutFeedback()
  const signalsEnabled = useSignalsEnabled()
  const { signals } = useOpportunitySignals(item?.id ?? null, isMaster ? true : !!signalsEnabled)
  const { openResultsDrawer } = useResultsDrawer()

  const header = (
    <div className="flex items-center gap-2">
      {item?.logo_url ? (
        <img
          src={item.logo_url || ''}
          alt={item?.name || item?.symbol || ''}
          className="h-6 w-6 rounded-full object-cover"
        />
      ) : (
        <div className="h-6 w-6 rounded-full bg-muted" />
      )}
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold">
          {item?.name || item?.symbol || ''}
        </div>
        <div className="truncate text-xs text-foreground/70">{item?.symbol || ''}</div>
      </div>
    </div>
  )

  const canToggle = !!item && Number.isFinite(Number(item.id))

  return (
    <BottomDrawer open={open} onOpenChange={onOpenChange} header={header}>
      <div className="space-y-2">
        {isMaster ? (
          <>
            {/* 1. Detalhes */}
            <button
              type="button"
              className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-left text-sm hover:bg-muted"
              onClick={withClose(() => {
                if (!item) return
                try {
                  onViewed?.(String(item.id))
                } catch {
                  // noop
                }
                openModal({
                  id: item.id,
                  symbol: item.symbol,
                  name: item.name,
                  logo_url: item.logo_url,
                  kind: 'opportunity',
                })
              })}
            >
              Detalhes
            </button>

            {/* 2. Resultado */}
            {item && resultsEnabled && (
              <button
                type="button"
                className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-left text-sm hover:bg-muted"
                onClick={withClose(() => {
                  openResultsDrawer({
                    opportunityId: Number(item.id),
                    symbol: item.symbol,
                    name: item.name,
                    logoUrl: item.logo_url,
                  })
                })}
              >
                Resultado
              </button>
            )}

            {/* 3. Estou dentro / Estou fora */}
            {item && (isMaster || inOutEnabled) && (
              <button
                type="button"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-60"
                disabled={inOut.loading || inOut.toggling || !canToggle}
                onClick={() => {
                  if (!canToggle || !item || inOut.loading || inOut.toggling) return
                  const targetIsIn = !inOut.isIn
                  void (async () => {
                    await inOut.toggle()
                    if (targetIsIn) {
                      showIn(item.symbol, item.name, {
                        opportunityId: item.id,
                        tfMandante: signals?.tfMandante ?? null,
                      })
                    } else {
                      showOut(item.symbol, item.name, {
                        opportunityId: item.id,
                        tfMandante: signals?.tfMandante ?? null,
                      })
                    }
                    onOpenChange(false)
                  })()
                }}
              >
                {inOut.isIn ? 'Estou fora' : 'Estou dentro'}
              </button>
            )}

            {/* 3. Relatar Bug */}
            <button
              type="button"
              className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-left text-sm hover:bg-muted"
              onClick={withClose(() => {
                if (!item) return
                openReport({
                  id: item.id,
                  symbol: item.symbol,
                  name: item.name,
                  logo_url: item.logo_url,
                  kind: 'opportunity',
                })
              })}
            >
              Relatar Bug
            </button>

            {/* 4. Copiar dados (opcional) */}
            {onCopyRequested && item && (
              <button
                type="button"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-left text-sm hover:bg-muted"
                onClick={withClose(() => onCopyRequested(String(item.id)))}
              >
                Copiar dados
              </button>
            )}

            {/* 5. Debug */}
            <button
              type="button"
              className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-left text-sm hover:bg-muted"
              onClick={withClose(() => {
                if (!item) return
                openDebug({
                  id: item.id,
                  symbol: item.symbol,
                  name: item.name,
                  logo_url: item.logo_url,
                  kind: 'opportunity',
                })
              })}
            >
              Debug
            </button>
          </>
        ) : (
          <>
            {/* 1. Ver gráfico */}
            <button
              type="button"
              className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-left text-sm hover:bg-muted"
              onClick={withClose(() => {
                if (!item) return
                try {
                  onViewed?.(String(item.id))
                } catch {
                  // noop
                }
                openModal({
                  id: item.id,
                  symbol: item.symbol,
                  name: item.name,
                  logo_url: item.logo_url,
                  kind: 'opportunity',
                })
              })}
            >
              Ver gráfico
            </button>

            {/* 2. Resultado */}
            {item && resultsEnabled && (
              <button
                type="button"
                className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-left text-sm hover:bg-muted"
                onClick={withClose(() => {
                  openResultsDrawer({
                    opportunityId: Number(item.id),
                    symbol: item.symbol,
                    name: item.name,
                    logoUrl: item.logo_url,
                  })
                })}
              >
                Resultado
              </button>
            )}

            {/* 3. Estou dentro / Estou fora */}
            {item && (isMaster || inOutEnabled) && (
              <button
                type="button"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-60"
                disabled={inOut.loading || inOut.toggling || !canToggle}
                onClick={() => {
                  if (!canToggle || !item || inOut.loading || inOut.toggling) return
                  const targetIsIn = !inOut.isIn
                  void (async () => {
                    await inOut.toggle()
                    if (targetIsIn) {
                      showIn(item.symbol, item.name, {
                        opportunityId: item.id,
                        tfMandante: signals?.tfMandante ?? null,
                      })
                    } else {
                      showOut(item.symbol, item.name, {
                        opportunityId: item.id,
                        tfMandante: signals?.tfMandante ?? null,
                      })
                    }
                    onOpenChange(false)
                  })()
                }}
              >
                {inOut.isIn ? 'Estou fora' : 'Estou dentro'}
              </button>
            )}

            {/* 3. Relatar Bug */}
            <button
              type="button"
              className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-left text-sm hover:bg-muted"
              onClick={withClose(() => {
                if (!item) return
                openReport({
                  id: item.id,
                  symbol: item.symbol,
                  name: item.name,
                  logo_url: item.logo_url,
                  kind: 'opportunity',
                })
              })}
            >
              Relatar Bug
            </button>

            {/* 4. Copiar dados (opcional) */}
            {onCopyRequested && item && (
              <button
                type="button"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-left text-sm hover:bg-muted"
                onClick={withClose(() => onCopyRequested(String(item.id)))}
              >
                Copiar dados
              </button>
            )}
          </>
        )}
      </div>
    </BottomDrawer>
  )
}
